import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import Header from "@/components/Header";
import { IndicativeBadge } from "@/components/IndicativeBadge";
import Footer from "@/components/Footer";
import SEOHead, { PAGE_SEO } from "@/components/SEOHead";
import PriceHistoryModal from "@/components/PriceHistoryModal";
import { useAuth } from "@/context/AuthContext";
import {
  CaretUp,
  CaretDown,
  Storefront,
  Minus,
  CircleNotch,
  X,
  Trophy,
  Scales,
  Medal,
  SortAscending,
  SortDescending,
  Bell,
  BellRinging,
  Warning,
  Check,
  Trash,
  PushPin,
  PushPinSlash,
  Eye
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { HeaderStickyBanner } from "@/components/AdPlacements";

// Import refactored components
import { MartFilters } from "@/components/marts/MartFilters";
import { MartTable } from "@/components/marts/MartTable";
import { ComparisonModal } from "@/components/marts/ComparisonModal";
import { LeagueTable } from "@/components/marts/LeagueTable";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

export default function IrishMartsPage() {
  const { user, token } = useAuth();
  const [prices, setPrices] = useState([]);
  const [stats, setStats] = useState(null);
  const [categories, setCategories] = useState([]);
  const [provinces, setProvinces] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Filters
  const [selectedCategory, setSelectedCategory] = useState("bullocks_400_500kg");
  const [selectedCounty, setSelectedCounty] = useState("");
  const [selectedProvince, setSelectedProvince] = useState("");
  const [sortBy, setSortBy] = useState("price_desc");
  const [searchQuery, setSearchQuery] = useState("");
  const [lastUpdated, setLastUpdated] = useState(null);

  // Comparison feature
  const [selectedMarts, setSelectedMarts] = useState([]);
  const [showComparison, setShowComparison] = useState(false);
  const [comparisonData, setComparisonData] = useState(null);
  const [comparisonLoading, setComparisonLoading] = useState(false);

  // County League Table
  const [leagueTable, setLeagueTable] = useState([]);
  const [leagueSortOrder, setLeagueSortOrder] = useState("desc");

  // Mart Alerts
  const [martAlerts, setMartAlerts] = useState([]);
  const [showAlertModal, setShowAlertModal] = useState(false);
  const [alertMart, setAlertMart] = useState(null);
  const [alertCondition, setAlertCondition] = useState("above");
  const [alertPrice, setAlertPrice] = useState("");
  const [alertLoading, setAlertLoading] = useState(false);

  // Watchlist
  const [watchlist, setWatchlist] = useState([]);
  const [watchlistNames, setWatchlistNames] = useState([]);
  const [watchlistLoading, setWatchlistLoading] = useState(false);

  // Price History Chart
  const [showPriceHistory, setShowPriceHistory] = useState(false);
  const [historyMart, setHistoryMart] = useState(null);

  // View toggle
  const [activeView, setActiveView] = useState("table");

  // Data fetching effects
  useEffect(() => {
    fetchInitialData();
  }, []);

  // Data fetching functions
  const fetchInitialData = async () => {
    try {
      const [categoriesRes, provincesRes, summaryRes] = await Promise.all([
        axios.get(`${API}/prices/irish-marts/categories`),
        axios.get(`${API}/prices/irish-marts/provinces`),
        axios.get(`${API}/prices/irish-marts/summary`)
      ]);
      setCategories(categoriesRes.data.categories);
      setProvinces(provincesRes.data.provinces);
      setSummary(summaryRes.data);
    } catch { /* Silent fail */ }
  };

  const fetchPrices = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/prices/irish-marts`, {
        params: {
          category: selectedCategory,
          county: selectedCounty || undefined,
          province: selectedProvince || undefined,
          sort_by: sortBy
        }
      });
      setPrices(response.data.prices);
      setStats(response.data.stats);
      setLastUpdated(response.data.last_updated);
    } catch { /* Silent fail */ }
    finally { setLoading(false); }
  }, [selectedCategory, selectedCounty, selectedProvince, sortBy]);

  const fetchLeagueTable = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/prices/irish-marts/county-league`, {
        params: { category: selectedCategory, sort_order: leagueSortOrder }
      });
      setLeagueTable(response.data.league_table);
    } catch { /* Silent fail */ }
  }, [selectedCategory, leagueSortOrder]);

  const fetchMartAlerts = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/user/mart-alerts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMartAlerts(response.data);
    } catch { /* Silent fail */ }
  }, [token]);

  const fetchWatchlist = useCallback(async () => {
    setWatchlistLoading(true);
    try {
      const response = await axios.get(`${API}/user/watchlist`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWatchlist(response.data.watchlist);
    } catch { /* Silent fail */ }
    finally { setWatchlistLoading(false); }
  }, [token]);

  const fetchWatchlistNames = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/user/watchlist/names`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWatchlistNames(response.data.watchlist);
    } catch { /* Silent fail */ }
  }, [token]);

  useEffect(() => {
    fetchPrices();
    fetchLeagueTable();
  }, [fetchPrices, fetchLeagueTable]);

  useEffect(() => {
    if (user && token) {
      fetchMartAlerts();
      fetchWatchlistNames();
    }
  }, [user, token, fetchMartAlerts, fetchWatchlistNames]);

  useEffect(() => {
    if (user && token && activeView === "watchlist") {
      fetchWatchlist();
    }
  }, [user, token, activeView, fetchWatchlist]);

  // Action handlers
  const toggleWatchlist = async (martName) => {
    if (!user) {
      toast.error("Please sign in to add marts to your watchlist");
      return;
    }
    if (watchlistNames.includes(martName)) {
      try {
        await axios.delete(`${API}/user/watchlist/${encodeURIComponent(martName)}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success(`${martName} removed from watchlist`);
        setWatchlistNames(prev => prev.filter(m => m !== martName));
        setWatchlist(prev => prev.filter(m => m.mart_name !== martName));
      } catch { toast.error("Failed to remove from watchlist"); }
    } else {
      try {
        await axios.post(`${API}/user/watchlist`, 
          { mart_name: martName },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success(`${martName} added to watchlist`);
        setWatchlistNames(prev => [...prev, martName]);
        if (activeView === "watchlist") fetchWatchlist();
      } catch (error) {
        toast.error(error.response?.data?.detail || "Failed to add to watchlist");
      }
    }
  };

  const toggleMartSelection = (martName) => {
    setSelectedMarts(prev => {
      if (prev.includes(martName)) return prev.filter(m => m !== martName);
      if (prev.length >= 6) return prev;
      return [...prev, martName];
    });
  };

  const fetchComparison = async () => {
    if (selectedMarts.length < 2) return;
    setComparisonLoading(true);
    try {
      const response = await axios.post(`${API}/prices/irish-marts/compare`, selectedMarts, {
        params: { category: selectedCategory, weeks: 8 }
      });
      setComparisonData(response.data);
      setShowComparison(true);
    } catch { /* Silent fail */ }
    finally { setComparisonLoading(false); }
  };

  const openAlertModal = (mart) => {
    if (!user) {
      toast.error("Please sign in to create price alerts");
      return;
    }
    setAlertMart(mart);
    setAlertPrice(mart.price.toFixed(2));
    setShowAlertModal(true);
  };

  const createMartAlert = async () => {
    if (!alertMart || !alertPrice) return;
    setAlertLoading(true);
    try {
      await axios.post(`${API}/user/mart-alerts`, {
        mart_name: alertMart.mart_name,
        category: selectedCategory,
        condition: alertCondition,
        target_price: parseFloat(alertPrice)
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Alert created for ${alertMart.mart_name}`);
      setShowAlertModal(false);
      setAlertMart(null);
      setAlertPrice("");
      fetchMartAlerts();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create alert");
    } finally { setAlertLoading(false); }
  };

  const deleteMartAlert = async (alertId) => {
    try {
      await axios.delete(`${API}/user/mart-alerts/${alertId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Alert deleted");
      fetchMartAlerts();
    } catch { toast.error("Failed to delete alert"); }
  };

  // Utility functions
  const formatPrice = (price, unit) => {
    if (!price) return "N/A";
    return unit === "per head" ? `€${price.toFixed(0)}` : `€${price.toFixed(2)}`;
  };

  const getChangeColor = (change) => {
    if (change > 0) return "text-[#059669]";
    if (change < 0) return "text-[#DC2626]";
    return "text-[#6B6661]";
  };

  const getChangeBg = (change) => {
    if (change > 0) return "bg-[#ECFDF5]";
    if (change < 0) return "bg-[#FEF2F2]";
    return "bg-[#F6E9DD]";
  };

  const getCounties = () => {
    if (selectedProvince) {
      const prov = provinces.find(p => p.name === selectedProvince);
      return prov ? prov.counties : [];
    }
    return [...new Set(provinces.flatMap(p => p.counties))].sort();
  };

  const getRankBadge = (rank) => {
    if (rank === 1) return <Trophy size={18} className="text-[#FCD34D]" weight="fill" />;
    if (rank === 2) return <Medal size={18} className="text-[#8B8580]" weight="fill" />;
    if (rank === 3) return <Medal size={18} className="text-[#B45309]" weight="fill" />;
    return <span className="text-[#6B6661] font-mono text-sm">{rank}</span>;
  };

  // Derived state
  const filteredPrices = prices.filter(p => 
    p.mart_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.county.toLowerCase().includes(searchQuery.toLowerCase())
  );
  const currentCategory = categories.find(c => c.id === selectedCategory);

  return (
    <div className="min-h-screen bg-[#022C22]">
      <SEOHead {...PAGE_SEO.irishMarts} url="/irish-marts" />
      <HeaderStickyBanner />
      <Header />
      
      <main className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-3">
            <span className="px-2 py-1 bg-[#0F766E] text-white text-xs font-mono uppercase tracking-wider">
              Live Markets
            </span>
            <span className="px-2 py-1 bg-[#FEF3C7] text-[#92400E] text-xs font-mono uppercase tracking-wider">
              {filteredPrices.length} Marts
            </span>
          </div>
          <h1 className="font-serif text-3xl sm:text-4xl text-white mb-2">Irish Livestock Marts</h1>
          <div className="mb-2"><IndicativeBadge /></div>
          <p className="text-[#6EE7B7] font-sans">
            Indicative prices across {prices.length}+ livestock marts in Ireland — live mart data feeds are being rolled out.
          </p>
        </div>

        {/* Market Indices */}
        {summary && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
            {summary.indices.map((index) => (
              <div key={index.symbol} className="bg-[#064E3B] border border-[#065F46] p-5">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <span className="text-xs font-mono text-[#6EE7B7] uppercase tracking-wider">{index.name}</span>
                    <span className="ml-2 px-1.5 py-0.5 bg-[#065F46] text-[#6EE7B7] text-xs font-mono">{index.symbol}</span>
                  </div>
                  <span className="text-xs text-[#6EE7B7]">{index.unit}</span>
                </div>
                <div className="flex items-baseline gap-4">
                  <span className="font-mono text-3xl text-white">
                    {index.unit === "per head" ? `€${index.value.toFixed(0)}` : `€${index.value.toFixed(2)}`}
                  </span>
                  <div className={`flex items-center gap-1 px-2 py-1 ${getChangeBg(index.change)}`}>
                    {index.change > 0 ? <CaretUp size={14} className="text-[#059669]" weight="fill" /> : 
                     index.change < 0 ? <CaretDown size={14} className="text-[#DC2626]" weight="fill" /> : 
                     <Minus size={14} className="text-[#6B6661]" />}
                    <span className={`font-mono text-sm ${getChangeColor(index.change)}`}>
                      {index.change >= 0 ? "+" : ""}{index.change.toFixed(2)}%
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* View Toggle Tabs */}
        <ViewToggleTabs 
          activeView={activeView}
          setActiveView={setActiveView}
          user={user}
          watchlistNames={watchlistNames}
          martAlerts={martAlerts}
        />

        {/* Filters - Using Refactored Component */}
        <MartFilters
          categories={categories}
          provinces={provinces.map(p => p.name)}
          selectedCategory={selectedCategory}
          setSelectedCategory={setSelectedCategory}
          selectedProvince={selectedProvince}
          setSelectedProvince={setSelectedProvince}
          selectedCounty={selectedCounty}
          setSelectedCounty={setSelectedCounty}
          sortBy={sortBy}
          setSortBy={setSortBy}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          counties={getCounties()}
        />

        {/* Compare Marts Bar */}
        {selectedMarts.length > 0 && activeView === "table" && (
          <CompareBar
            selectedMarts={selectedMarts}
            setSelectedMarts={setSelectedMarts}
            fetchComparison={fetchComparison}
            comparisonLoading={comparisonLoading}
          />
        )}

        {/* COUNTY LEAGUE TABLE VIEW - Using Refactored Component */}
        {activeView === "league" && (
          <LeagueTable
            leagueTable={leagueTable}
            leagueSortOrder={leagueSortOrder}
            onToggleSort={() => setLeagueSortOrder(prev => prev === "desc" ? "asc" : "desc")}
            currentCategory={currentCategory}
            formatPrice={formatPrice}
          />
        )}

        {/* ALL MARTS TABLE VIEW - Using Refactored Component */}
        {activeView === "table" && (
          <MartTable
            loading={loading}
            prices={filteredPrices}
            selectedMarts={selectedMarts}
            watchlistNames={watchlistNames}
            martAlerts={martAlerts}
            selectedCategory={selectedCategory}
            onToggleSelection={toggleMartSelection}
            onToggleWatchlist={toggleWatchlist}
            onShowHistory={(mart) => { setHistoryMart(mart); setShowPriceHistory(true); }}
            onShowAlert={openAlertModal}
            formatPrice={formatPrice}
            getChangeColor={getChangeColor}
            getChangeBg={getChangeBg}
            currentCategory={currentCategory}
            user={user}
          />
        )}

        {/* WATCHLIST VIEW */}
        {activeView === "watchlist" && (
          <WatchlistView
            watchlistLoading={watchlistLoading}
            watchlist={watchlist}
            watchlistNames={watchlistNames}
            setActiveView={setActiveView}
            removeFromWatchlist={(name) => toggleWatchlist(name)}
            getChangeColor={getChangeColor}
          />
        )}

        {/* User's Active Alerts */}
        {user && martAlerts.length > 0 && activeView !== "watchlist" && (
          <ActiveAlertsSection
            martAlerts={martAlerts}
            deleteMartAlert={deleteMartAlert}
            formatPrice={formatPrice}
            currentCategory={currentCategory}
          />
        )}

        {/* Stats Footer */}
        {stats && (
          <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard label="Average" value={formatPrice(stats.avg_price, currentCategory?.unit)} />
            <StatCard label="Highest" value={formatPrice(stats.max_price, currentCategory?.unit)} color="text-[#059669]" />
            <StatCard label="Lowest" value={formatPrice(stats.min_price, currentCategory?.unit)} color="text-[#DC2626]" />
            <StatCard label="Total" value={stats.total_marts} />
          </div>
        )}
      </main>

      {/* Comparison Modal - Using Refactored Component */}
      <ComparisonModal
        show={showComparison}
        onClose={() => setShowComparison(false)}
        comparisonData={comparisonData}
        currentCategory={currentCategory}
        formatPrice={formatPrice}
      />

      {/* Alert Modal */}
      {showAlertModal && alertMart && (
        <AlertModalInline
          alertMart={alertMart}
          alertCondition={alertCondition}
          setAlertCondition={setAlertCondition}
          alertPrice={alertPrice}
          setAlertPrice={setAlertPrice}
          alertLoading={alertLoading}
          createMartAlert={createMartAlert}
          setShowAlertModal={setShowAlertModal}
          currentCategory={currentCategory}
          formatPrice={formatPrice}
          user={user}
          martAlerts={martAlerts}
        />
      )}

      {/* Price History Modal */}
      {showPriceHistory && historyMart && (
        <PriceHistoryModal
          mart={historyMart}
          category={selectedCategory}
          categoryName={currentCategory?.name || 'Bullocks 400-500kg'}
          onClose={() => { setShowPriceHistory(false); setHistoryMart(null); }}
        />
      )}

      <Footer />
    </div>
  );
}

// Sub-components to keep main component clean

function ViewToggleTabs({ activeView, setActiveView, user, watchlistNames, martAlerts }) {
  return (
    <div className="flex items-center gap-2 mb-6 flex-wrap">
      <button
        onClick={() => setActiveView("table")}
        data-testid="view-table"
        className={`px-4 py-2 font-sans text-sm transition-colors ${
          activeView === "table" ? "bg-[#0F766E] text-white" : "bg-[#065F46] text-white/70 hover:text-white"
        }`}
      >
        <Storefront size={16} className="inline mr-2" />All Marts
      </button>
      <button
        onClick={() => setActiveView("league")}
        data-testid="view-league"
        className={`px-4 py-2 font-sans text-sm transition-colors ${
          activeView === "league" ? "bg-[#0F766E] text-white" : "bg-[#065F46] text-white/70 hover:text-white"
        }`}
      >
        <Trophy size={16} className="inline mr-2" />County League
      </button>
      {user && (
        <button
          onClick={() => setActiveView("watchlist")}
          data-testid="view-watchlist"
          className={`px-4 py-2 font-sans text-sm transition-colors ${
            activeView === "watchlist" ? "bg-[#0F766E] text-white" : "bg-[#065F46] text-white/70 hover:text-white"
          }`}
        >
          <Eye size={16} className="inline mr-2" />My Watchlist
          {watchlistNames.length > 0 && (
            <span className="ml-2 px-1.5 py-0.5 bg-[#FCD34D] text-[#0A0A0A] text-xs font-mono rounded-full">
              {watchlistNames.length}
            </span>
          )}
        </button>
      )}
      {user && martAlerts.length > 0 && (
        <div className="ml-auto flex items-center gap-2 px-3 py-1.5 bg-[#FEF3C7] text-[#92400E]">
          <BellRinging size={16} />
          <span className="text-xs font-mono">{martAlerts.length} Alerts</span>
        </div>
      )}
    </div>
  );
}

function CompareBar({ selectedMarts, setSelectedMarts, fetchComparison, comparisonLoading }) {
  return (
    <div className="mb-4 p-3 bg-[#0F766E]/20 border border-[#0F766E] flex items-center justify-between flex-wrap gap-2">
      <div className="flex items-center gap-2 flex-wrap">
        <Scales size={18} className="text-[#14B8A6]" />
        <span className="text-white text-sm">{selectedMarts.length} selected for comparison</span>
        <div className="flex gap-1 flex-wrap">
          {selectedMarts.map(mart => (
            <span key={mart} className="px-2 py-0.5 bg-[#065F46] text-white text-xs flex items-center gap-1">
              {mart}
              <button onClick={() => setSelectedMarts(prev => prev.filter(m => m !== mart))} className="hover:text-[#FCD34D]">
                <X size={12} />
              </button>
            </span>
          ))}
        </div>
      </div>
      <button
        onClick={fetchComparison}
        disabled={selectedMarts.length < 2 || comparisonLoading}
        className="px-4 py-1.5 bg-[#FCD34D] text-[#0A0A0A] text-sm font-medium hover:bg-[#FBBF24] disabled:opacity-50 flex items-center gap-1"
      >
        {comparisonLoading ? <CircleNotch size={14} className="animate-spin" /> : <Scales size={14} className="inline mr-1" />}
        Compare
      </button>
    </div>
  );
}

function WatchlistView({ watchlistLoading, watchlist, watchlistNames, setActiveView, removeFromWatchlist, getChangeColor }) {
  return (
    <div className="bg-white border border-[#E0D4C8] overflow-hidden">
      <div className="p-4 border-b border-[#E0D4C8] flex items-center justify-between">
        <h2 className="font-serif text-xl text-[#33302E] flex items-center gap-2">
          <Eye size={24} className="text-[#F59E0B]" />My Watchlist
        </h2>
        <span className="text-xs text-[#6B6661] font-mono">{watchlistNames.length}/10 marts</span>
      </div>

      {watchlistLoading ? (
        <div className="px-4 py-12 text-center">
          <CircleNotch size={32} className="animate-spin text-[#0F766E] mx-auto mb-2" />
          <span className="text-[#6B6661] text-sm">Loading your watchlist...</span>
        </div>
      ) : watchlist.length === 0 ? (
        <div className="px-4 py-12 text-center">
          <PushPin size={48} className="text-[#374151] mx-auto mb-4" />
          <p className="text-[#8B8580] mb-2">Your watchlist is empty</p>
          <p className="text-[#6B6661] text-sm">Pin your favourite marts from the "All Marts" tab</p>
          <button onClick={() => setActiveView("table")} className="mt-4 px-4 py-2 bg-[#0F766E] text-white text-sm hover:bg-[#14B8A6]">
            Browse Marts
          </button>
        </div>
      ) : (
        <div className="divide-y divide-[#E5E7EB]">
          {watchlist.map((mart) => (
            <div key={mart.mart_name} className="p-4 hover:bg-[#F9FAFB]">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <Storefront size={20} className="text-[#0F766E]" />
                  <div>
                    <h3 className="text-[#33302E] font-medium">{mart.mart_name}</h3>
                    <p className="text-[#6B6661] text-sm">{mart.county} • {mart.province} • {mart.market_day}</p>
                  </div>
                </div>
                <button onClick={() => removeFromWatchlist(mart.mart_name)} className="text-[#6B6661] hover:text-[#DC2626] p-1" title="Remove">
                  <PushPinSlash size={18} />
                </button>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {Object.entries(mart.prices).map(([category, data]) => (
                  <div key={category} className="bg-[#F9FAFB] p-3">
                    <p className="text-[#6B6661] text-xs mb-1 truncate">{category.replace(/_/g, ' ')}</p>
                    <div className="flex items-baseline gap-2">
                      <span className="font-mono text-sm text-[#33302E]">
                        {data.unit === "per head" ? `€${data.price.toFixed(0)}` : `€${data.price.toFixed(2)}`}
                      </span>
                      <span className={`text-xs ${getChangeColor(data.change_percent)}`}>
                        {data.change_percent >= 0 ? "+" : ""}{data.change_percent.toFixed(1)}%
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ActiveAlertsSection({ martAlerts, deleteMartAlert, formatPrice, currentCategory }) {
  return (
    <div className="mt-6 bg-[#111827] border border-[#1F2937] p-4">
      <h3 className="text-sm font-mono uppercase tracking-wider text-[#FCD34D] mb-3 flex items-center gap-2">
        <BellRinging size={16} />Your Mart Alerts
      </h3>
      <div className="space-y-2">
        {martAlerts.map((alert) => (
          <div key={alert.id} className={`flex items-center justify-between p-3 border ${alert.triggered ? 'border-[#059669] bg-[#059669]/10' : 'border-[#1F2937]'}`}>
            <div className="flex items-center gap-3">
              {alert.triggered ? <Check size={18} className="text-[#059669]" /> : <Bell size={18} className="text-[#FCD34D]" />}
              <div>
                <p className="text-white text-sm">{alert.mart_name}</p>
                <p className="text-[#8B8580] text-xs">
                  {alert.condition === "above" ? "↑" : "↓"} {formatPrice(alert.target_price, currentCategory?.unit)}
                </p>
              </div>
            </div>
            <button onClick={() => deleteMartAlert(alert.id)} className="text-[#6B6661] hover:text-[#DC2626] p-1">
              <Trash size={16} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function StatCard({ label, value, color = "text-white" }) {
  return (
    <div className="bg-[#111827] border border-[#1F2937] p-4">
      <span className="text-xs font-mono text-[#6B6661] uppercase">{label}</span>
      <p className={`font-mono text-xl mt-1 ${color}`}>{value}</p>
    </div>
  );
}

function AlertModalInline({ alertMart, alertCondition, setAlertCondition, alertPrice, setAlertPrice, alertLoading, createMartAlert, setShowAlertModal, currentCategory, formatPrice, user, martAlerts }) {
  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
      <div className="bg-[#111827] border border-[#1F2937] w-full max-w-md p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-serif text-xl text-white flex items-center gap-2">
            <Bell size={24} className="text-[#FCD34D]" />Set Price Alert
          </h2>
          <button onClick={() => setShowAlertModal(false)} className="text-[#8B8580] hover:text-white"><X size={24} /></button>
        </div>

        <div className="mb-4 p-3 bg-[#1F2937]">
          <p className="text-white font-medium">{alertMart.mart_name}</p>
          <p className="text-[#8B8580] text-sm">{alertMart.county} • {currentCategory?.name}</p>
          <p className="text-[#0F766E] font-mono mt-1">Current: {formatPrice(alertMart.price, alertMart.unit)}</p>
        </div>

        <div className="mb-4">
          <label className="text-[#8B8580] text-sm mb-2 block">Alert me when price goes:</label>
          <div className="flex gap-2">
            <button onClick={() => setAlertCondition("above")} className={`flex-1 py-2 ${alertCondition === "above" ? "bg-[#059669] text-white" : "bg-[#1F2937] text-[#8B8580]"}`}>
              <CaretUp size={16} className="inline mr-1" />Above
            </button>
            <button onClick={() => setAlertCondition("below")} className={`flex-1 py-2 ${alertCondition === "below" ? "bg-[#DC2626] text-white" : "bg-[#1F2937] text-[#8B8580]"}`}>
              <CaretDown size={16} className="inline mr-1" />Below
            </button>
          </div>
        </div>

        <div className="mb-6">
          <label className="text-[#8B8580] text-sm mb-2 block">Target Price ({currentCategory?.unit || "€/kg"})</label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B6661]">€</span>
            <input type="number" step="0.01" value={alertPrice} onChange={(e) => setAlertPrice(e.target.value)}
              className="w-full pl-8 pr-4 py-2 bg-[#1F2937] border border-[#374151] text-white font-mono text-lg focus:outline-none focus:border-[#0F766E]" />
          </div>
        </div>

        {user?.subscription === "free" && martAlerts.length >= 2 && (
          <div className="mb-4 p-3 bg-[#FEF3C7] text-[#92400E] text-sm flex items-center gap-2">
            <Warning size={18} />Free tier limited to 2 alerts. Upgrade for unlimited.
          </div>
        )}

        <div className="flex gap-2">
          <button onClick={() => setShowAlertModal(false)} className="flex-1 py-2 bg-[#1F2937] text-[#8B8580] hover:text-white">Cancel</button>
          <button onClick={createMartAlert} disabled={alertLoading || !alertPrice}
            className="flex-1 py-2 bg-[#0F766E] text-white hover:bg-[#14B8A6] disabled:opacity-50 flex items-center justify-center gap-2">
            {alertLoading ? <CircleNotch size={16} className="animate-spin" /> : <Bell size={16} />}Create Alert
          </button>
        </div>
      </div>
    </div>
  );
}
